import { useEffect, useState } from "react";
import { api, apiError, getSessionTokenForTest } from "@/lib/api";
import { todayISO, fmtDateDMY } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { Download, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Simplified Manager's Daily Entry.
 *
 * Design goal (per operator feedback): most of what the manager types every day
 * is just today's sales headline + the SBI balance. Everything else — shift
 * operational flags, nozzle counts, air-filling toggles — is folded into a
 * collapsible "Advanced" section. Fuel purchase inputs were removed entirely —
 * they now live under Fuel Purchase (PO → Invoice) so the daily form isn't
 * overwhelming.
 */

const BASIC_FIELDS = [
  ["hsd_sales_l", "HSD Sales (L)", "number"],
  ["ms_sales_l", "MS Sales (L)", "number"],
  ["cng_sales_kg", "CNG Sales (kg)", "number"],
  ["lubricants_sale_rs", "Lubricants Sale (₹)", "number"],
  ["sbi_balance", "SBI Balance (₹)", "number"],
];

const ADVANCED_FIELDS = [
  ["hsd_shortage_l", "HSD Shortage (L)", "number"],
  ["ms_shortage_l", "MS Shortage (L)", "number"],
  ["regular_trial_hsd", "Regular Trial HSD (L)", "number"],
  ["regular_trial_ms", "Regular Trial MS (L)", "number"],
  ["hsd_excess_trial", "HSD Excess Trial", "yn"],
  ["ms_excess_trial", "MS Excess Trial", "yn"],
  ["shift1_operational", "1st Shift Operational", "yn"],
  ["shift2_operational", "2nd Shift Operational", "yn"],
  ["shift3_operational", "3rd Shift Operational", "yn"],
  ["fillers_shift1", "Fillers 1st shift", "number"],
  ["fillers_shift2", "Fillers 2nd shift", "number"],
  ["fillers_shift3", "Fillers 3rd shift", "number"],
  ["hsd_nozzles_shift1", "HSD Nozzles 1st shift", "number"],
  ["hsd_nozzles_shift2", "HSD Nozzles 2nd shift", "number"],
  ["hsd_nozzles_shift3", "HSD Nozzles 3rd shift", "number"],
  ["ms_nozzles_shift1", "MS Nozzles 1st shift", "number"],
  ["ms_nozzles_shift2", "MS Nozzles 2nd shift", "number"],
  ["ms_nozzles_shift3", "MS Nozzles 3rd shift", "number"],
  ["cng_nozzles_shift1", "CNG Nozzles 1st shift", "number"],
  ["cng_nozzles_shift2", "CNG Nozzles 2nd shift", "number"],
  ["cng_nozzles_shift3", "CNG Nozzles 3rd shift", "number"],
  ["air_shift1", "Air Filling 1st shift", "yn"],
  ["air_shift2", "Air Filling 2nd shift", "yn"],
  ["air_shift3", "Air Filling 3rd shift", "yn"],
  ["twelve_hr_shift", "12hrs Shift?", "yn"],
];

export default function ManagerDaily() {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState({});
  const [mode, setMode] = useState("3-shift");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [monthly, setMonthly] = useState(todayISO().slice(0, 7));
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/manager/daily/${date}`);
      setData(r.data || {});
      const cfg = await api.get("/masters/config");
      setMode(cfg.data?.SHIFT_MODE || "3-shift");
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [date]); // eslint-disable-line

  const set = (k, v) => setData({ ...data, [k]: v });

  const save = async () => {
    try {
      await api.post("/manager/daily", { date, ...data });
      toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadMonthlyPack = async () => {
    setDownloading(true);
    try {
      const url = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/exports/monthly_pack.xlsx?month=${monthly}`;
      const token = getSessionTokenForTest();
      const res = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `manager_pack_${monthly}.xlsx`;
      link.click();
      toast.success("Monthly pack downloaded ✓");
    } catch (e) {
      toast.error(apiError(e) || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const isThirdShiftField = (k) => /3(rd)?$|shift3|_shift3$/.test(k);
  const twoShift = mode === "2-shift";

  const renderField = ([k, label, type]) => {
    const disabled = twoShift && isThirdShiftField(k);
    return (
      <div key={k}>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</Label>
        {type === "yn" ? (
          <div className="flex gap-1 mt-1">
            {["Y", "N"].map((v) => (
              <button
                key={v}
                type="button"
                disabled={disabled}
                data-testid={`daily-${k}-${v}`}
                onClick={() => set(k, v)}
                className={
                  "px-3 h-10 border-2 font-bold text-sm rounded-sm " +
                  (data[k] === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300") +
                  (disabled ? " opacity-40" : "")
                }
              >
                {v}
              </button>
            ))}
          </div>
        ) : (
          <Input
            data-testid={`daily-${k}`}
            type="number"
            step="0.01"
            value={data[k] ?? ""}
            onChange={(e) => set(k, Number(e.target.value))}
            className="num h-10 border border-slate-300 mt-1"
            disabled={disabled}
          />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="manager-daily-root">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Manager&rsquo;s Daily Entry
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Sales fields prefill from shift entries — you usually only need to type the SBI balance.
          </p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="block h-11 border border-slate-300 rounded-sm px-3 bg-white"
            />
          </div>
          <Button
            data-testid="save-daily-btn"
            onClick={save}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            Save
          </Button>
        </div>
      </div>

      <Card className="p-4 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-3 text-sm uppercase tracking-wider">
          Sales headline for {fmtDateDMY(date)}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
          {BASIC_FIELDS.map(renderField)}
        </div>
        <div className="text-xs text-slate-500 mt-2">
          Fuel purchases have moved to <a href="/fuel-purchase" className="underline">Fuel Purchase (PO → Invoice)</a>
          {" "}so this form stays lean. Bank balance is manual today — automation is planned once your bank feed
          integration is set up under <a href="/integrations" className="underline">Integrations</a>.
        </div>
      </Card>

      <Card className="p-4 border-2 border-slate-300 rounded-sm">
        <button
          type="button"
          data-testid="daily-toggle-advanced"
          className="w-full flex items-center justify-between font-bold text-slate-800 text-sm uppercase tracking-wider"
          onClick={() => setShowAdvanced((v) => !v)}
        >
          <span>Advanced — shift operational, nozzle counts, air, trials</span>
          {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {showAdvanced && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {ADVANCED_FIELDS.map(renderField)}
          </div>
        )}
      </Card>

      <Card className="p-4 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2 text-sm uppercase tracking-wider">Monthly pack export</h3>
        <p className="text-xs text-slate-500 mb-3">
          One Excel with Sales Summary, all receipts, inflows, outflows, POs + invoices, debtor ledger,
          salary sheet and red flags — everything Mili needs to close the month.
        </p>
        <div className="flex gap-2 items-end">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Month</label>
            <input
              type="month"
              data-testid="monthly-pack-month"
              value={monthly}
              onChange={(e) => setMonthly(e.target.value)}
              className="block h-10 border border-slate-300 rounded-sm px-3 bg-white"
            />
          </div>
          <Button
            data-testid="monthly-pack-btn"
            onClick={downloadMonthlyPack}
            disabled={downloading}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Download size={14} className="mr-1" />
            {downloading ? "Preparing…" : "Download monthly pack"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
